import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  COUNTRY_OPTIONS,
  DEFAULT_MAP_CENTER,
  getApiErrorDetail,
  useApiClient,
  useAutocomplete,
} from '@klareospace/pharos-lib-front-utils';
import type { AddressInputInitialAddress, AddressInputLabels } from '@klareospace/pharos-lib-front-utils';
import { createGeocodingApi } from '@klareospace/pharos-lib-front-utils';
import type { GeocodingApi } from '@klareospace/pharos-lib-front-utils';
import { joinStrings, toCountryName } from '@klareospace/pharos-lib-utils';
import type { AutocompleteSuggestion, GeocodingSource, PostalAddress } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { LocateFixed, Navigation } from 'lucide-react';
import type { Control, FieldValues, Path, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Checkbox } from '../../ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../ui/form';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { AddressMap } from '../address/address-map';
import { AddressSearchInput } from '../address/address-search-input';
import { applySuggestionToAddressForm } from '../address/apply-suggestion-to-address-form';
import { formatSuggestionAddress } from '../address/format-suggestion-address';
import { ScoreBadge } from '../address/score-badge';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressInputFields {
  'address.additionalInformations'?: string;
  'address.city': string;
  'address.complement'?: string;
  'address.country': string;
  'address.digicode'?: string;
  'address.floor'?: string;
  'address.hasElevator'?: boolean;
  'address.houseNumber': string;
  'address.postalCode': string;
  'address.street': string;
  'coordinates.latitude'?: number;
  'coordinates.longitude'?: number;
  geocodingSource?: GeocodingSource;
}

export type { AddressInputInitialAddress };

// `AddressInputLabels` is owned by `pharos-lib-front-utils` (paired with `useAddressInputLabels`)
// to avoid a front-utils -> web-utils dependency cycle; re-exported here for existing consumers.
export type { AddressInputLabels };

export interface AddressInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  /** When provided, the component initialises in "selected" state showing existing address data. */
  initialAddress?: AddressInputInitialAddress;
  labels: AddressInputLabels;
  /**
   * Same shape as RHF's `form.setValue` — pass it directly, no cast needed. Internally this
   * component sets several unrelated field paths (address.*, coordinates.*, geocodingSource) that
   * TFieldValues does not statically guarantee exist, so a single cast back to a loose signature
   * is applied once at the top of `AddressInput` rather than at every internal call site.
   */
  onSetValue: UseFormSetValue<TFieldValues>;
  /** Tile layer attribution (e.g. HERE, OSM/CARTO) — the app owns the tile provider choice. */
  tileAttribution: string;
  /** Tile layer URL template — the app owns the tile provider choice (e.g. HERE Maps logistics.day). */
  tileUrl: string;
}

// ─── Field Paths ──────────────────────────────────────────────────────────────

/**
 * Every field path this component reads or writes, named once here instead of a scattered
 * `'address.foo' as Path<TFieldValues>` cast at each call site below.
 */
const ADDRESS_INPUT_FIELD_PATHS = {
  additionalInformations: 'address.additionalInformations',
  city: 'address.city',
  complement: 'address.complement',
  country: 'address.country',
  digicode: 'address.digicode',
  floor: 'address.floor',
  geocodingRawScore: 'geocodingRawScore',
  geocodingScore: 'geocodingScore',
  geocodingSource: 'geocodingSource',
  hasElevator: 'address.hasElevator',
  houseNumber: 'address.houseNumber',
  latitude: 'coordinates.latitude',
  longitude: 'coordinates.longitude',
  postalCode: 'address.postalCode',
  root: 'address',
  street: 'address.street',
} as const;

function addressInputField<TFieldValues extends FieldValues>(
  key: keyof typeof ADDRESS_INPUT_FIELD_PATHS,
): Path<TFieldValues> {
  return ADDRESS_INPUT_FIELD_PATHS[key] as Path<TFieldValues>;
}

/** Step precision for the manual latitude/longitude number inputs (6 decimal places, ~0.1m resolution). */
const COORDINATE_INPUT_STEP = '0.000001';

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseGeocodeMutationOptions {
  api: GeocodingApi;
  labels: AddressInputLabels;
  onCoords: (latitude: number, longitude: number) => void;
  onScore?: (rawScore: number) => void;
}

function useGeocodeMutation({
  api,
  labels,
  onCoords,
  onScore,
}: UseGeocodeMutationOptions): UseMutationResult<void, Error, PostalAddress> {
  return useMutation<void, Error, PostalAddress>({
    mutationFn: async (address: PostalAddress): Promise<void> => {
      const result = await api.geocode(address);
      if ('coords' in result) {
        onCoords(result.coords.latitude, result.coords.longitude);
        // Pass the raw 0–1 score through untouched — applyScore() converts it to the 0–100 UX
        // scale for display and also exposes the raw value via the 'geocodingRawScore' form
        // field for consumers that need full precision.
        onScore?.(result.score);
        return;
      }
      const errorMessage =
        'message' in result && typeof result.message === 'string' && result.message.length > 0
          ? result.message
          : labels.geocodeError;
      throw new Error(errorMessage);
    },
    onError: (error: Error) => {
      const detail = getApiErrorDetail(error);
      toast.error(detail.message || labels.geocodeError);
    },
  });
}

// ─── Address State Hook ───────────────────────────────────────────────────────

type Coordinates = { lat: number; lng: number };

interface UseAddressStateOptions<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  initialAddress?: AddressInputProps<TFieldValues>['initialAddress'];
  labels: AddressInputLabels;
  onSetValue: (field: Path<TFieldValues>, value: unknown) => void;
}

interface InitialAddressState {
  coords: Coordinates | null;
  hasAddress: boolean;
  query: string;
}

function buildInitialQuery(addr: NonNullable<AddressInputProps<FieldValues>['initialAddress']>): string {
  const streetPart = [addr.houseNumber, addr.street].filter(Boolean).join(' ');
  const countryName = addr.country ? toCountryName(addr.country) : '';
  const cityPart = [addr.postalCode, addr.city, countryName].filter(Boolean).join(' ');
  return joinStrings([streetPart, cityPart].filter(Boolean));
}

function extractInitialCoords(coordinates: { latitude?: number; longitude?: number } | undefined): Coordinates | null {
  if (coordinates?.latitude === undefined || coordinates?.longitude === undefined) return null;
  return { lat: coordinates.latitude, lng: coordinates.longitude };
}

function deriveInitialState(initialAddress: AddressInputProps<FieldValues>['initialAddress']): InitialAddressState {
  const hasAddress = Boolean(initialAddress?.city);
  const query = hasAddress && initialAddress ? buildInitialQuery(initialAddress) : '';
  const coords = extractInitialCoords(initialAddress?.coordinates);
  return { coords, hasAddress, query };
}

function clearAddressFields<TFieldValues extends FieldValues>(
  onSetValue: (field: Path<TFieldValues>, value: unknown) => void,
): void {
  const fields = ['houseNumber', 'street', 'city', 'postalCode', 'country'] as const;
  for (const field of fields) {
    onSetValue(addressInputField<TFieldValues>(field), '');
  }
  onSetValue(addressInputField<TFieldValues>('latitude'), undefined);
  onSetValue(addressInputField<TFieldValues>('longitude'), undefined);
}

function useApplyCoords<TFieldValues extends FieldValues>(
  onSetValue: (field: Path<TFieldValues>, value: unknown) => void,
  setCoords: React.Dispatch<React.SetStateAction<Coordinates | null>>,
): (latitude: number, longitude: number) => void {
  return useCallback(
    (latitude: number, longitude: number): void => {
      onSetValue(addressInputField<TFieldValues>('latitude'), latitude);
      onSetValue(addressInputField<TFieldValues>('longitude'), longitude);
      setCoords({ lat: latitude, lng: longitude });
    },
    [onSetValue, setCoords],
  );
}

interface UseSuggestionSelectOptions<TFieldValues extends FieldValues> {
  applyCoords: (latitude: number, longitude: number) => void;
  applyScore: (score: number) => void;
  geocodeMutation: UseMutationResult<void, Error, PostalAddress>;
  onSetValue: (field: Path<TFieldValues>, value: unknown) => void;
  setIsSelected: React.Dispatch<React.SetStateAction<boolean>>;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}

function useSuggestionSelect<TFieldValues extends FieldValues>({
  applyCoords,
  applyScore,
  geocodeMutation,
  onSetValue,
  setIsSelected,
  setQuery,
}: UseSuggestionSelectOptions<TFieldValues>): (suggestion: AutocompleteSuggestion) => void {
  return useCallback(
    (suggestion: AutocompleteSuggestion): void => {
      if (geocodeMutation.isPending) return;

      setQuery(formatSuggestionAddress(suggestion.address) || suggestion.title);
      setIsSelected(true);
      onSetValue(addressInputField<TFieldValues>('geocodingSource'), 'autocomplete');

      const { address: addressValues, coords: suggestionCoords } = applySuggestionToAddressForm(suggestion, onSetValue);

      if (suggestionCoords !== null) {
        applyCoords(suggestionCoords.lat, suggestionCoords.lng);
        // The suggestion already carries coordinates — no separate geocode call is made, so
        // there is no provider score to report. Treat it as full confidence (raw score 1).
        applyScore(1);
        return;
      }

      const { city, country, houseNumber, postalCode, street } = addressValues;

      if (city && country) {
        geocodeMutation.mutate({ city, country, houseNumber, postalCode, street });
      }
    },
    [applyCoords, applyScore, geocodeMutation, onSetValue, setQuery, setIsSelected],
  );
}

/** Watched `address.*` field values as returned by `useWatch({ name: 'address' })` — RHF has no static type for a dynamic path. */
type WatchedAddressValues = Record<string, string> | undefined;

interface UseGeocodeClickOptions<TFieldValues extends FieldValues> {
  addressValues: WatchedAddressValues;
  geocodeMutation: UseMutationResult<void, Error, PostalAddress>;
  onSetValue: (field: Path<TFieldValues>, value: unknown) => void;
  setIsSelected: React.Dispatch<React.SetStateAction<boolean>>;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}

function useGeocodeClick<TFieldValues extends FieldValues>({
  addressValues,
  geocodeMutation,
  onSetValue,
  setIsSelected,
  setQuery,
}: UseGeocodeClickOptions<TFieldValues>): () => void {
  return useCallback((): void => {
    if (!addressValues?.city || !addressValues?.country) return;
    geocodeMutation.mutate(
      {
        city: addressValues.city,
        country: addressValues.country,
        houseNumber: addressValues.houseNumber,
        postalCode: addressValues.postalCode,
        street: addressValues.street,
      },
      {
        onSuccess: () => {
          setIsSelected(true);
          onSetValue(addressInputField<TFieldValues>('geocodingSource'), 'manual');
          const fullAddress = formatSuggestionAddress({
            city: addressValues.city,
            countryName: toCountryName(addressValues.country),
            houseNumber: addressValues.houseNumber,
            postalCode: addressValues.postalCode,
            street: addressValues.street,
          });
          setQuery(fullAddress);
        },
      },
    );
  }, [addressValues, geocodeMutation, onSetValue, setIsSelected, setQuery]);
}

interface UseAddressStateReturn {
  coords: Coordinates | null;
  geocodeMutation: UseMutationResult<void, Error, PostalAddress>;
  geocodingScore: number | null;
  handleBackToSearch: () => void;
  handleEnterManually: () => void;
  handleGeocodeClick: () => void;
  handleMapPositionChange: (lat: number, lng: number) => void;
  handleQueryChange: (newQuery: string) => void;
  handleSuggestionSelect: (suggestion: AutocompleteSuggestion) => void;
  hasAddressForGeocode: boolean;
  isLoading: boolean;
  isManualMode: boolean;
  isSelected: boolean;
  query: string;
  suggestions: AutocompleteSuggestion[];
}

function useAddressState<TFieldValues extends FieldValues>({
  control,
  initialAddress,
  labels,
  onSetValue,
}: UseAddressStateOptions<TFieldValues>): UseAddressStateReturn {
  const client = useApiClient('geo');
  const api = useMemo(() => createGeocodingApi(client), [client]);

  const initial = useMemo(() => deriveInitialState(initialAddress), [initialAddress]);

  const [query, setQuery] = useState(initial.query);
  const [isManualMode, setIsManualMode] = useState(initialAddress?.geocodingSource === 'manual');
  const [coords, setCoords] = useState<Coordinates | null>(initial.coords);
  const [isSelected, setIsSelected] = useState(initial.hasAddress);
  const [geocodingScore, setGeocodingScore] = useState<number | null>(initialAddress?.geocodingScore ?? null);

  const { isLoading, suggestions } = useAutocomplete({ enabled: !isSelected && !isManualMode, query });

  const applyCoords = useApplyCoords<TFieldValues>(onSetValue, setCoords);

  // Converts the raw 0–1 score to the 0–100 UX scale for display and also exposes the raw value
  // via the 'geocodingRawScore' form field for consumers that need full precision.
  const applyScore = useCallback(
    (rawScore: number): void => {
      const percentScore = Math.round(rawScore * 100);
      setGeocodingScore(percentScore);
      onSetValue(addressInputField<TFieldValues>('geocodingScore'), percentScore);
      onSetValue(addressInputField<TFieldValues>('geocodingRawScore'), rawScore);
    },
    [onSetValue],
  );

  const geocodeMutation = useGeocodeMutation({ api, labels, onCoords: applyCoords, onScore: applyScore });

  const handleSuggestionSelect = useSuggestionSelect({
    applyCoords,
    applyScore,
    geocodeMutation,
    onSetValue,
    setIsSelected,
    setQuery,
  });

  const handleQueryChange = useCallback(
    (newQuery: string): void => {
      setQuery(newQuery);
      if (isSelected) {
        setGeocodingScore(null);
        onSetValue(addressInputField<TFieldValues>('geocodingScore'), undefined);
        onSetValue(addressInputField<TFieldValues>('geocodingRawScore'), undefined);
        setIsSelected(false);
        clearAddressFields<TFieldValues>(onSetValue);
        setCoords(null);
      }
    },
    [isSelected, onSetValue],
  );

  const handleMapPositionChange = useCallback(
    (lat: number, lng: number): void => {
      onSetValue(addressInputField<TFieldValues>('latitude'), lat);
      onSetValue(addressInputField<TFieldValues>('longitude'), lng);
      setCoords({ lat, lng });
      // Manual pin placement (drag or typed lat/lng) is treated as full confidence (raw score 1).
      applyScore(1);
    },
    [applyScore, onSetValue],
  );

  const addressValues = useWatch({ control, name: addressInputField<TFieldValues>('root') }) as WatchedAddressValues;

  const watchedHouseNumber = useWatch({ control, name: addressInputField<TFieldValues>('houseNumber') }) as
    string | undefined;
  const watchedStreet = useWatch({ control, name: addressInputField<TFieldValues>('street') }) as string | undefined;
  const watchedCity = useWatch({ control, name: addressInputField<TFieldValues>('city') }) as string | undefined;
  const hasAddressForGeocode = !!(watchedStreet && watchedCity);

  const prevManualMode = React.useRef(isManualMode);

  useEffect(() => {
    if (!isManualMode) {
      prevManualMode.current = false;
      return;
    }
    // Skip the initial switch to manual mode — preserve the existing query.
    // Only update query when the user edits street/houseNumber while already in manual mode.
    if (!prevManualMode.current) {
      prevManualMode.current = true;
      return;
    }
    const parts = [watchedHouseNumber, watchedStreet].filter(Boolean);
    setQuery(parts.join(' '));
  }, [isManualMode, watchedHouseNumber, watchedStreet]);

  const handleEnterManually = useCallback((): void => {
    setIsManualMode(true);
    setQuery('');
  }, []);

  const handleBackToSearch = useCallback((): void => {
    setIsManualMode(false);
    // Restore "selected" state if address data is still present (i.e. user
    // didn't clear the fields while in manual mode).
    if (watchedCity) {
      setIsSelected(true);
    }
  }, [watchedCity]);

  const handleGeocodeClick = useGeocodeClick({ addressValues, geocodeMutation, onSetValue, setIsSelected, setQuery });

  return {
    coords,
    geocodeMutation,
    geocodingScore,
    handleBackToSearch,
    handleEnterManually,
    handleGeocodeClick,
    handleMapPositionChange,
    handleQueryChange,
    handleSuggestionSelect,
    hasAddressForGeocode,
    isLoading,
    isManualMode,
    isSelected,
    query,
    suggestions,
  };
}

// ─── Manual Address Fields ────────────────────────────────────────────────────

interface ManualAddressFieldsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  coords: Coordinates | null;
  geocodingScore: number | null;
  hasAddressForGeocode: boolean;
  isGeocoding: boolean;
  labels: AddressInputLabels;
  onGeocodeClick: () => void;
  onMapPositionChange: (lat: number, lng: number) => void;
}

function ManualAddressFields<TFieldValues extends FieldValues>({
  control,
  coords,
  geocodingScore,
  hasAddressForGeocode,
  isGeocoding,
  labels,
  onGeocodeClick,
  onMapPositionChange,
}: ManualAddressFieldsProps<TFieldValues>): React.JSX.Element {
  return (
    <div className="space-y-3 pl-6 border-l-2 border-primary/20">
      {/* Number + Street */}
      <div className="grid grid-cols-[100px_1fr] gap-3">
        <FormField
          control={control}
          name={addressInputField<TFieldValues>('houseNumber')}
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{labels.houseNumber}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={labels.houseNumberPlaceholder} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={addressInputField<TFieldValues>('street')}
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{labels.street}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={labels.streetPlaceholder} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Postal Code + City */}
      <div className="grid grid-cols-2 gap-3">
        <FormField
          control={control}
          name={addressInputField<TFieldValues>('postalCode')}
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{labels.postalCode}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={labels.postalCodePlaceholder} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={addressInputField<TFieldValues>('city')}
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{labels.city}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={labels.cityPlaceholder} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Country */}
      <FormField
        control={control}
        name={addressInputField<TFieldValues>('country')}
        render={({ field }): React.JSX.Element => (
          <FormItem>
            <FormLabel>{labels.country}</FormLabel>
            <Select onValueChange={field.onChange} value={field.value ?? ''}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={labels.country} />
                </SelectTrigger>
              </FormControl>
              <SelectContent position="popper" className="max-h-60 overflow-y-auto">
                {COUNTRY_OPTIONS.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Coordinates + Geocode */}
      <div className="space-y-2">
        <span className="text-sm font-medium leading-none">{labels.coordinates}</span>
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              className="pl-9 text-sm"
              onChange={(e): void => {
                const lat = parseFloat(e.target.value);
                if (!isNaN(lat)) onMapPositionChange(lat, coords?.lng ?? 0);
              }}
              placeholder={labels.latitude}
              step={COORDINATE_INPUT_STEP}
              type="number"
              value={coords?.lat ?? ''}
            />
          </div>
          <div className="relative flex-1">
            <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground rotate-90" />
            <Input
              className="pl-9 text-sm"
              onChange={(e): void => {
                const lng = parseFloat(e.target.value);
                if (!isNaN(lng)) onMapPositionChange(coords?.lat ?? 0, lng);
              }}
              placeholder={labels.longitude}
              step={COORDINATE_INPUT_STEP}
              type="number"
              value={coords?.lng ?? ''}
            />
          </div>
          {geocodingScore != null && (
            <ScoreBadge className="relative top-auto right-auto translate-y-0" score={geocodingScore} />
          )}
          <button
            className="flex items-center gap-1.5 px-3 h-9 shrink-0 rounded-md border-2 border-dashed border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/50 transition-colors text-sm font-medium"
            disabled={isGeocoding || !hasAddressForGeocode}
            onClick={onGeocodeClick}
            type="button"
          >
            <LocateFixed className="w-4 h-4" />
            {labels.geocode}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AddressInput<TFieldValues extends FieldValues>({
  control,
  initialAddress,
  labels,
  onSetValue,
  tileAttribution,
  tileUrl,
}: AddressInputProps<TFieldValues>): React.JSX.Element {
  // Widened once at the boundary: internally this component sets several unrelated field paths
  // (address.*, coordinates.*, geocodingSource) that TFieldValues does not statically guarantee,
  // so a single cast here replaces the per-call-site `as never` this prop used to force on callers.
  const setValue = onSetValue as (field: Path<TFieldValues>, value: unknown) => void;

  const {
    coords,
    geocodeMutation,
    geocodingScore,
    handleBackToSearch,
    handleEnterManually,
    handleGeocodeClick,
    handleMapPositionChange,
    handleQueryChange,
    handleSuggestionSelect,
    hasAddressForGeocode,
    isLoading,
    isManualMode,
    isSelected,
    query,
    suggestions,
  } = useAddressState({ control, initialAddress, labels, onSetValue: setValue });

  return (
    <div className="flex flex-col gap-4">
      {!isManualMode && (
        <AddressSearchInput
          emptyResultsLabel={labels.emptyResults}
          enterManuallyLabel={labels.enterManually}
          isLoading={isLoading}
          isSelected={isSelected}
          onEnterManually={handleEnterManually}
          onQueryChange={handleQueryChange}
          onSelect={handleSuggestionSelect}
          placeholder={labels.searchPlaceholder}
          query={query}
          score={geocodingScore}
          searchAriaLabel={labels.searchAriaLabel}
          suggestions={suggestions}
        />
      )}

      {isManualMode ? (
        <button
          className="text-muted-foreground hover:text-foreground w-fit text-sm underline-offset-4 hover:underline"
          onClick={handleBackToSearch}
          type="button"
        >
          {labels.backToSearch}
        </button>
      ) : (
        <button
          className="text-muted-foreground hover:text-foreground w-fit text-sm underline-offset-4 hover:underline"
          onClick={handleEnterManually}
          type="button"
        >
          {labels.cantFind} <span className="font-medium">{labels.enterManually}</span>
        </button>
      )}

      {isManualMode && (
        <ManualAddressFields
          control={control}
          coords={coords}
          geocodingScore={geocodingScore}
          hasAddressForGeocode={hasAddressForGeocode}
          isGeocoding={geocodeMutation.isPending}
          labels={labels}
          onGeocodeClick={handleGeocodeClick}
          onMapPositionChange={handleMapPositionChange}
        />
      )}

      {/* Address Line 2 (complement) — visible in all modes */}
      <FormField
        control={control}
        name={addressInputField<TFieldValues>('complement')}
        render={({ field }): React.JSX.Element => (
          <FormItem>
            <FormLabel>{labels.addressLine2}</FormLabel>
            <FormControl>
              <Input {...field} placeholder={labels.addressLine2Placeholder} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Floor + Digicode — visible in all modes */}
      <div className="grid grid-cols-2 gap-3">
        <FormField
          control={control}
          name={addressInputField<TFieldValues>('floor')}
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{labels.floor}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={labels.floorPlaceholder} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={addressInputField<TFieldValues>('digicode')}
          render={({ field }): React.JSX.Element => (
            <FormItem>
              <FormLabel>{labels.digicode}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={labels.digicodePlaceholder} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Has Elevator — visible in all modes */}
      <FormField
        control={control}
        name={addressInputField<TFieldValues>('hasElevator')}
        render={({ field }): React.JSX.Element => (
          <FormItem className="flex flex-row gap-2 space-y-0">
            <FormControl>
              <Checkbox checked={field.value ?? false} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel className="cursor-pointer mt-0.5">{labels.hasElevator}</FormLabel>
          </FormItem>
        )}
      />

      <AddressMap
        attribution={tileAttribution}
        lat={coords?.lat ?? DEFAULT_MAP_CENTER.lat}
        lng={coords?.lng ?? DEFAULT_MAP_CENTER.lng}
        onPositionChange={handleMapPositionChange}
        tileUrl={tileUrl}
      />

      {/* Access Instructions (additionalInformations) — visible in all modes */}
      <FormField
        control={control}
        name={addressInputField<TFieldValues>('additionalInformations')}
        render={({ field }): React.JSX.Element => (
          <FormItem>
            <FormLabel>{labels.accessInstructions}</FormLabel>
            <FormControl>
              <Input {...field} placeholder={labels.accessInstructionsPlaceholder} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
